
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipeService, productService } from '../lib/firebaseService';
import { Plus, Search, Edit2, Trash2, GlassWater, Loader2, AlertCircle, X, Check } from 'lucide-react';
import { t } from '../utils/translation';
import '../styles/components-extended.css';
import { useAuth } from '../context/AuthContext';

export default function Recipes() {
    const { userRole } = useAuth();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecipe, setEditingRecipe] = useState(null);

    // Queries
    const { data: recipes, isLoading: loadingRecipes } = useQuery({
        queryKey: ['recipes'],
        queryFn: recipeService.getRecipes
    });

    const { data: products } = useQuery({
        queryKey: ['products'],
        queryFn: productService.getProducts
    });

    // Mutations
    const deleteMutation = useMutation({
        mutationFn: recipeService.deleteRecipe,
        onSuccess: () => queryClient.invalidateQueries(['recipes'])
    });

    // Computed Logic: Available Servings
    const getRecipeAnalysis = (recipe) => {
        if (!recipe.ingredients || recipe.ingredients.length === 0 || !products) return { servings: 0, missing: [], status: 'no_ingredients' };

        let maxServings = Infinity;
        const missing = [];

        // Helper: Normalize to ml
        const normalizeToMl = (qty, unit) => {
            const val = parseFloat(qty || 0);
            switch (unit?.toLowerCase()) {
                case 'l': return val * 1000;
                case 'cl': return val * 10;
                case 'ml': return val;
                case 'kg': return val * 1000; // Treat as grams
                case 'g': return val;
                default: return val; // Unit/Piece
            }
        };

        recipe.ingredients.forEach(ing => {
            const product = products.find(p => p.id === ing.productId);
            if (!product) {
                missing.push(ing.name || 'Unknown');
                maxServings = 0;
            } else if (product.stock <= 0) {
                missing.push(product.name);
                maxServings = 0;
            } else {
                // Calculate total available in base units (ml or g)
                const productVolumeInMl = normalizeToMl(
                    product.volumeAmount || 1,
                    product.volumeUnit || 'unit'
                );
                const totalAvailableInMl = product.stock * productVolumeInMl;

                // Calculate required per serving in base units
                const requiredPerServingInMl = normalizeToMl(ing.qty, ing.unit);

                // Calculate servings from this ingredient
                const servingsFromIng = Math.floor(totalAvailableInMl / requiredPerServingInMl);

                if (servingsFromIng < maxServings) maxServings = servingsFromIng;
            }
        });

        if (maxServings === Infinity) maxServings = 0;

        return {
            servings: maxServings,
            missing,
            status: maxServings > 0 ? 'available' : 'unavailable'
        };
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('COMMON.CONFIRM'))) {
            await deleteMutation.mutateAsync(id);
        }
    };

    const filteredRecipes = recipes?.filter(r =>
        r.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    if (loadingRecipes) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-purple-600" size={32} /></div>;

    return (
        <div className="max-w-[1200px] mx-auto">
            {/* Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('RECIPES.TITLE')}</h1>
                    <p className="page-subtitle">{t('RECIPES.SUBTITLE')}</p>
                </div>
                <button onClick={() => { setEditingRecipe(null); setIsModalOpen(true); }} className="btn btn-primary">
                    <Plus size={20} className="mr-2" />
                    {t('RECIPES.ADD_RECIPE')}
                </button>
            </div>

            {/* Search */}
            <div className="content-card mb-6 p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder={t('RECIPES.SEARCH_PLACEHOLDER')}
                        className="input pl-10 w-full"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {/* Grid */}
            {filteredRecipes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRecipes.map(recipe => {
                        const analysis = getRecipeAnalysis(recipe);
                        return (
                            <div key={recipe.id} className="content-card hover:shadow-lg transition-shadow border-t-4 border-t-transparent hover:border-t-purple-500">
                                <div className="p-5">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                                            <GlassWater size={24} />
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => { setEditingRecipe(recipe); setIsModalOpen(true); }} className="p-1.5 text-gray-400 hover:text-purple-600">
                                                <Edit2 size={16} />
                                            </button>
                                            <button onClick={() => handleDelete(recipe.id)} className="p-1.5 text-gray-400 hover:text-red-600">
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-bold text-gray-900 mb-1">{recipe.name}</h3>
                                    <div className="text-sm text-gray-500 mb-4">{recipe.ingredients.length} {t('RECIPES.INGREDIENTS')}</div>

                                    <div className="flex items-center gap-3 mb-4">
                                        <div className={`text-2xl font-bold ${analysis.status === 'available' ? 'text-green-600' : 'text-red-500'}`}>
                                            {analysis.servings}
                                        </div>
                                        <div className="text-xs uppercase font-bold text-gray-400 tracking-wider">
                                            {t('RECIPES.AVAILABLE')} <br /> {t('RECIPES.SERVINGS')}
                                        </div>
                                    </div>

                                    {analysis.missing.length > 0 && (
                                        <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700 flex items-start gap-2">
                                            <AlertCircle size={14} className="shrink-0 mt-0.5" />
                                            <span>Missing: {analysis.missing.slice(0, 3).join(', ')}{analysis.missing.length > 3 && '...'}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="content-card text-center py-16">
                    <GlassWater className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-bold text-gray-900">{t('RECIPES.NO_RECIPES.TITLE')}</h3>
                    <p className="text-gray-500 max-w-sm mx-auto mt-2 mb-6">{t('RECIPES.NO_RECIPES.DESC')}</p>
                    <button onClick={() => setIsModalOpen(true)} className="btn btn-primary">
                        {t('RECIPES.NO_RECIPES.BTN')}
                    </button>
                </div>
            )}

            {isModalOpen && (
                <RecipeModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    initialData={editingRecipe}
                    products={products}
                />
            )}
        </div>
    );
}

function RecipeModal({ isOpen, onClose, initialData, products }) {
    const queryClient = useQueryClient();
    const [name, setName] = useState(initialData?.name || '');
    const [instructions, setInstructions] = useState(initialData?.instructions || '');
    const [ingredients, setIngredients] = useState(initialData?.ingredients || []); // [{ productId, qty, unit }]

    const mutation = useMutation({
        mutationFn: (data) => initialData ? recipeService.updateRecipe(initialData.id, data) : recipeService.addRecipe(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['recipes']);
            onClose();
        }
    });

    const addIngredient = () => {
        setIngredients([...ingredients, { productId: '', qty: 1, unit: 'cl' }]);
    };

    const updateIngredient = (index, field, value) => {
        const newIngs = [...ingredients];
        newIngs[index][field] = value;
        setIngredients(newIngs);
    };

    const removeIngredient = (index) => {
        setIngredients(ingredients.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validIngredients = ingredients.filter(i => i.productId && i.qty > 0);
        mutation.mutate({
            name,
            instructions,
            ingredients: validIngredients
        });
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content max-w-2xl">
                <div className="modal-header">
                    <h3 className="modal-title">{initialData ? t('RECIPES.MODAL.EDIT_TITLE') : t('RECIPES.MODAL.ADD_TITLE')}</h3>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="modal-body space-y-6">
                    <div>
                        <label className="form-label">{t('RECIPES.MODAL.NAME')}</label>
                        <input className="input w-full" value={name} onChange={e => setName(e.target.value)} required />
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="form-label mb-0">{t('RECIPES.INGREDIENTS')}</label>
                            <button type="button" onClick={addIngredient} className="text-sm font-bold text-purple-600 hover:text-purple-700">
                                + {t('RECIPES.MODAL.ADD_INGREDIENT')}
                            </button>
                        </div>

                        <div className="space-y-4 bg-gray-50 p-4 rounded-lg">{/* Increased from space-y-3 for better readability */}
                            {ingredients.map((ing, i) => (
                                <div key={i} className="flex gap-2 items-start">
                                    <div className="flex-1">
                                        <select
                                            className="input w-full text-sm py-2"
                                            value={ing.productId}
                                            onChange={e => updateIngredient(i, 'productId', e.target.value)}
                                            required
                                        >
                                            <option value="">{t('RECIPES.MODAL.SELECT_PRODUCT')}</option>
                                            {products?.map(p => (
                                                <option key={p.id} value={p.id}>{p.brand} {p.name} (Stock: {p.stock})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="w-24">
                                        <input
                                            type="number"
                                            className="input w-full text-sm py-2"
                                            placeholder="Qty"
                                            value={ing.qty}
                                            onChange={e => updateIngredient(i, 'qty', parseFloat(e.target.value))}
                                            step="0.1"
                                            min="0"
                                            required
                                        />
                                    </div>
                                    <div className="w-24">
                                        <select
                                            className="input w-full text-sm py-2"
                                            value={ing.unit || 'cl'}
                                            onChange={e => updateIngredient(i, 'unit', e.target.value)}
                                        >
                                            <option value="ml">ml</option>
                                            <option value="cl">cl</option>
                                            <option value="l">L</option>
                                            <option value="unit">Unit</option>
                                        </select>
                                    </div>
                                    <button type="button" onClick={() => removeIngredient(i)} className="p-2 text-gray-400 hover:text-red-500">
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                            {ingredients.length === 0 && (
                                <p className="text-center text-gray-400 text-sm italic py-2">No ingredients added yet.</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="form-label">{t('RECIPES.MODAL.INSTRUCTIONS')}</label>
                        <textarea
                            className="input w-full h-24 resize-none"
                            value={instructions}
                            onChange={e => setInstructions(e.target.value)}
                            placeholder="Mixing instructions..."
                        />
                    </div>

                    <div className="modal-footer">
                        <button type="button" onClick={onClose} className="btn btn-secondary">{t('COMMON.CANCEL')}</button>
                        <button type="submit" disabled={mutation.isPending} className="btn btn-primary">
                            {mutation.isPending ? <Loader2 className="animate-spin mr-2" /> : <Check className="mr-2" size={18} />}
                            {t('COMMON.SAVE')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
